// Real-time subscription hook
// 
// NOTE: Realtime subscriptions are not supported with the direct fetch approach.
// Use polling with React Query's refetchInterval instead.
//
// Example:
// const { data } = useQuery({
//   queryKey: ['timetable'],
//   queryFn: () => getTimetableRecordsDirect(filters),
//   refetchInterval: 5000, // Poll every 5 seconds
// })

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { timetableKeys } from './useTimetable'

// Options for real-time timetable subscription
export interface RealtimeTimetableOptions {
  enabled?: boolean
  onChangeDetected?: (payload: any) => void
}

// Subscribe to timetable changes (using polling)
export function useRealtimeTimetable(
  schoolId: string,
  options: RealtimeTimetableOptions = {}
) {
  const { enabled = true, onChangeDetected } = options
  const queryClient = useQueryClient()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!enabled || !schoolId) return

    // Use polling instead of realtime subscriptions
    // Refetch timetable data every 30 seconds
    intervalRef.current = setInterval(() => {
      console.log('Polling timetable changes for school:', schoolId)
      
      // Invalidate timetable queries to refetch data
      queryClient.invalidateQueries({ queryKey: timetableKeys.all })
      
      // Notify about potential changes (polling doesn't provide payload)
      if (onChangeDetected) {
        onChangeDetected({
          eventType: 'POLL',
          new: null,
          old: null,
        })
      }
    }, 30000) // Poll every 30 seconds

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [schoolId, enabled, onChangeDetected, queryClient])

  return null // No channel to return with polling
}

// Subscribe to absence changes (using polling)
export function useRealtimeAbsences(schoolId: string, enabled: boolean = true) {
  const queryClient = useQueryClient()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!enabled || !schoolId) return

    intervalRef.current = setInterval(() => {
      console.log('Polling absence changes for school:', schoolId)
      queryClient.invalidateQueries({ queryKey: ['absences'] })
    }, 5000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [schoolId, enabled, queryClient])

  return null
}

// Subscribe to assignment changes (using polling)
export function useRealtimeAssignments(schoolId: string, enabled: boolean = true) {
  const queryClient = useQueryClient()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!enabled || !schoolId) return

    intervalRef.current = setInterval(() => {
      console.log('Polling assignment changes for school:', schoolId)
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
    }, 5000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [schoolId, enabled, queryClient])

  return null
}

// Subscribe to teacher-specific assignments (using polling)
export function useRealtimeTeacherAssignments(
  teacherId: string,
  enabled: boolean = true,
  onNewAssignment?: (payload: any) => void
) {
  const queryClient = useQueryClient()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!enabled || !teacherId) return

    intervalRef.current = setInterval(() => {
      console.log('Polling teacher assignment changes for teacher:', teacherId)
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      
      // Note: Polling doesn't provide INSERT events, so we can't detect new assignments
      // Consider using a different approach if this is critical
    }, 5000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [teacherId, enabled, queryClient, onNewAssignment])

  return null
}

// Subscribe to all changes (using polling)
export function useRealtimeAll(schoolId: string, enabled: boolean = true) {
  useRealtimeTimetable(schoolId, { enabled })
  useRealtimeAbsences(schoolId, enabled)
  useRealtimeAssignments(schoolId, enabled)
}
